/**
 * @fileoverview GBP group text input for accounts using the Yext Org account.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */

import {useDebounce} from '/src/com/yext/listingslaunchstorm/js/common/hooks/usedebounce';
import {useFetch} from '/src/com/yext/listingslaunchstorm/js/common/hooks/usefetch';
import {SubHeader} from '/src/com/yext/listingslaunchstorm/js/components/common/subheader';
import {validateGoogleGroup} from '/src/com/yext/listingslaunchstorm/js/datasource/validategooglegroup';
import {Button} from '/ui/components/button/button';
import {Input} from '/ui/components/input/input';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/groupmenucontroller.module.scss';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';

/**
 *
 * @param {{
 *  linkedAccountId: number,
 *  initialGroupId?: string,
 *  onValidInput: function(string): void,
 * }} props
 */
export function GroupTextInput({
  linkedAccountId,
  initialGroupId,
  onValidInput,
}) {
  const [groupIdInput, setGroupIdInput] = React.useState('');
  const [inProgress, setInProgress] = React.useState(false);
  const {status, data, refetch} = useFetch(validateGoogleGroup);

  const validate = React.useCallback((accountId, newGroupId) => {
    if (newGroupId.length > 0) {
      refetch(accountId, newGroupId);
      setInProgress(false);
    }
  }, [refetch]);

  const {debouncedCallback} = useDebounce(validate, 1000);

  const isError = status === 'error';
  const isLoading = groupIdInput.length === 0 || inProgress || status === 'loading';
  const isValid = data !== undefined && data.valid;
  const isInvalid = data !== undefined && !data.valid;

  React.useEffect(() => {
    if (groupIdInput.length < 1) {
      setGroupIdInput(initialGroupId || '');
    }
  }, [groupIdInput.length, initialGroupId]);

  React.useEffect(() => {
    if (inProgress) {
      debouncedCallback(linkedAccountId, groupIdInput);
    } else if (isValid) {
      onValidInput(groupIdInput);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isValid, groupIdInput]);

  let validationMessage = undefined;
  if (isError) {
    validationMessage = (
      <div className={styles.groupError}>
        {yext.msg('There was an error validating this Location Group ID.')}&nbsp;
        <Button chromeless onClick={() => validate(linkedAccountId, groupIdInput)}>
          {yext.msg('Please click to retry.')}
        </Button>
      </div>
    );
  } else if (isValid) {
    validationMessage = (
      <div className={styles.groupValid}>
        <CheckedSvg />
        {yext.msg('Valid!')}
      </div>
    );
  } else if (isInvalid) {
    validationMessage = (
      <div className={styles.groupError}>
        {yext.msg('Invalid. ID may be incorrect or unauthorized.')}
      </div>
    );
  }

  return (
    <div>
      <SubHeader
        mainText={yext.msg('Location Group')}
        extraText={yext.msg('Please enter you Location Group ID from your Google Business Profile account')}
      />
      <div className={styles.textInput}>
        {!isLoading && validationMessage}
        <div className={styles.textInputText}>
          <Input
            label={yext.msg('Location Group')}
            hideLabel={true}
            type="text"
            valid={isLoading || (!isError && !isInvalid)}
            value={groupIdInput}
            onChange={newGroupId => {
              setGroupIdInput(newGroupId);
              setInProgress(true);
            }}
            disabled={false}
            placeholder={yext.msg('Enter Location Group ID')}
          />
        </div>
      </div>
    </div>
  );
}
